import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ContactApiModel, UpsertContactApiModel } from './model/targeting-search-result.api.model';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { CsvContactApiModel, CsvImportModel, FailedCsvContactApiModel } from './model/csv-contact-api.model';
import { TargetingQueryApi } from './model/targeting-search.api.model';
import { PrivateDataApiModel } from './model/private-data-api.model';
import { InteractionApiModel } from './model/interaction-api.model';
import { HandleModel } from '../../Discover/Targeting/models/handle.model';
import { NeptuneApi } from '../genericApi/neptune.api';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { PatchPersonalization } from '../distributions/model/distribution-personalization.api.model';
import { EmailAccountConnectorMessageApi } from './model/email-account-connector-message-api';
import { NylasThreadApi } from '../distributions/model/nylas-thread.api';
import { RepliesSearchApi } from './model/replies-search-api';
import { SortOrderEnum } from '../shared/enums/sort-order.enum';

@Injectable()
export class ContactsPublicApi implements OnDestroy {

    private subscriptions: Subscription[] = [];
    private locale: string = 'en-US';

    constructor(
        private neptuneApi: NeptuneApi,
        private currentUserStateService: CurrentUserStateService,
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getDataReferenceLanguageTag().pipe(
                filter(x => !!x)
            ).subscribe(languageTag => {
                this.locale = languageTag;
            })
        );
    }


    getContact(id: string): Observable<ContactApiModel> {
        const slug = `/contacts/public/${id}?locale=${this.locale}`;

        return this.neptuneApi.api<ContactApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    createContact(contact: ContactApiModel, listIds: number[]): Observable<string> {
        const slug = '/contacts/public/';
        const body: UpsertContactApiModel = {
            contact: contact,
            listIds: listIds
        };

        return this.neptuneApi.api<string>(
            slug,
            body,
            HttpVerbsEnum.POST,
            null,
            'text'
        );
    }

    importContacts(
        contacts: CsvContactApiModel[],
        replaceWithSystemContacts: boolean,
        reportDestinationEmails: string[],
        listsToCreate: string[]
    ): Observable<FailedCsvContactApiModel[]> {
        const slug = `/contacts/public/batchimport?locale=${this.locale}`;
        const body: CsvImportModel = {
            contacts: contacts,
            replaceWithSystemContacts: replaceWithSystemContacts,
            reportDestinationEmails: reportDestinationEmails,
            listsToCreate: listsToCreate
        };

        return this.neptuneApi.api<FailedCsvContactApiModel[]>(
            slug,
            body,
            HttpVerbsEnum.POST
        );
    }

    editContact(contact: ContactApiModel, listIds: number[]): Observable<string> {
        const slug = `/contacts/public/${contact.id}`;
        const body: UpsertContactApiModel = {
            contact: contact,
            listIds: listIds
        };

        return this.neptuneApi.api<string>(
            slug,
            body,
            HttpVerbsEnum.PUT
        );
    }

    deleteContacts(ids: string[]): Observable<void> {
        const slug = '/contacts/public/deletebatch';

        return this.neptuneApi.api<void>(
            slug,
            ids,
            HttpVerbsEnum.POST
        );
    }

    deletePrivatesMatchingSearch(query: TargetingQueryApi): Observable<void> {
        const slug = '/contacts/public/deletesearchresults';

        return this.neptuneApi.api<void>(
            slug,
            query,
            HttpVerbsEnum.POST
        );
    }

    updatePersonalizations(contactId: string, patchModel: PatchPersonalization): Observable<string> {
        const slug = `/contacts/public/${contactId}/personalizations`;

        return this.neptuneApi.api<string>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    setPrivateData(contactId: string, privateData: PrivateDataApiModel): Observable<string> {
        const slug = `/contacts/public/${contactId}/privatedata`;

        return this.neptuneApi.api<string>(
            slug,
            privateData,
            HttpVerbsEnum.PUT
        );
    }

    setPreferredEmail(contactId: string, handle: HandleModel): Observable<string> {
        const slug = `/contacts/public/${contactId}/preferredemail?email=${handle.value}&isPrivateEmail=${handle.isPrivateData}`;

        return this.neptuneApi.api<string>(
            slug,
            null,
            HttpVerbsEnum.POST
        );
    }

    getPrivateData(id: string): Observable<PrivateDataApiModel> {
        const slug = `/contacts/public/${id}/privatedata?locale=${this.locale}`;

        return this.neptuneApi.api<PrivateDataApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getInteractions(entityId: string, rows: number): Observable<InteractionApiModel[]> {

        const slug: string = `/contacts/public/${entityId}/interactions?rows=${rows}&sortOrder=${SortOrderEnum.descending}`;

        return this.neptuneApi.api<InteractionApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    directMessage(emailAccountConnectorMessage: EmailAccountConnectorMessageApi, contactId: string): Observable<void> {
        const slug = `/contacts/public/message/${contactId}/direct`;

        return this.neptuneApi.api<void>(
            slug,
            emailAccountConnectorMessage,
            HttpVerbsEnum.POST
        );
    }

    getDistributionMessage(repliesSearchApiModel: RepliesSearchApi, contactId: string): Observable<NylasThreadApi> {
        const slug = `/contacts/public/message/${contactId}/replies`;

        return this.neptuneApi.api(
            slug,
            repliesSearchApiModel,
            HttpVerbsEnum.POST
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
